import { LockClosedIcon } from '@heroicons/react/24/outline'
import { NavLink, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { Crown } from 'iconoir-react'
import { Tooltip } from 'app/components/Tooltip'

export type TabType = {
  name: string
  href: string
  totalItems?: string
  disabled?: boolean
  disabledMessage?: string
  superUserOnly?: boolean
}

type Args = {
  tabs: TabType[]
  className?: string
}

export type TabsViewProps = {
  tabs: TabType[]
  className?: string
  search?: string
  activeHref?: string
}

export function TabsView({ tabs, className, search = ``, activeHref }: TabsViewProps) {
  return (
    <div className="block">
      <div className="flex items-baseline space-x-4 border-b border-default" aria-label="Tabs">
        {tabs?.map((tab) => {
          const isActive = activeHref === tab.href
          return (
            <a
              key={tab.name}
              href={tab.href + search}
              className={classNames(
                className,
                isActive ? `border-b-2 border-solid border-blue-600 text-blue-900` : `text-subtle hover:text-default`,
                tab.disabled && `!text-minimal`,
                `bg-transparent px-1 pb-3 pt-0.5 text-ui-sm`,
              )}
              onClick={(e) => {
                if (tab.disabled) e.preventDefault()
              }}
            >
              <Tooltip
                message={tab.disabled && tab.disabledMessage}
                className={tab.disabled ? `cursor-not-allowed` : `cursor-pointer`}
              >
                <div className="flex items-center gap-1 align-middle">
                  {tab.name}
                  {tab.disabled && <LockClosedIcon className="ml-1 h-4 w-4" />}
                  {tab.superUserOnly && (
                    <div className="size-5 flex items-center justify-center rounded bg-amber-500/[0.08]">
                      <Crown width={12} height={12} className="text-amber-800" />
                    </div>
                  )}
                  {tab.totalItems && (
                    <span
                      className={classNames(
                        `min-w-5 ml-2 rounded-3xl px-1 py-0.5 text-center text-caption-sm`,
                        isActive ? `bg-blue-600 text-on-color` : `bg-surface-strong text-default`,
                      )}
                    >
                      {tab.totalItems}
                    </span>
                  )}
                </div>
              </Tooltip>
            </a>
          )
        })}
      </div>
    </div>
  )
}

export default function Tabs({ tabs, className }: Args) {
  const { search } = useLocation()

  return (
    <div className="block">
      <div className="flex items-baseline space-x-4 border-b border-default" aria-label="Tabs">
        {tabs?.map((tab) => (
          <NavLink
            key={tab.name}
            className={({ isActive }) =>
              classNames(
                className,
                isActive ? `border-b-2 border-solid border-blue-600 text-blue-900` : `text-subtle hover:text-default`,
                tab.disabled && `!text-minimal`,
                `bg-transparent px-1 pb-3 pt-0.5 text-ui-sm`,
              )
            }
            to={tab.href + search}
            onClick={(e) => {
              if (tab.disabled) {
                e.preventDefault()
              }
            }}
          >
            {({ isActive }) => (
              <Tooltip
                message={tab.disabled && tab.disabledMessage}
                className={tab.disabled ? `cursor-not-allowed` : `cursor-pointer`}
              >
                <div className="flex items-center gap-1 align-middle">
                  {tab.name}
                  {tab.disabled && <LockClosedIcon className="ml-1 h-4 w-4" />}
                  {tab.superUserOnly && (
                    <div className="size-5 flex items-center justify-center rounded bg-amber-500/[0.08]">
                      <Crown width={12} height={12} className="text-amber-800" />
                    </div>
                  )}
                  {tab.totalItems && (
                    <span
                      className={classNames(
                        `min-w-5 ml-2 rounded-3xl px-1 py-0.5 text-center text-caption-sm`,
                        isActive ? `bg-blue-600 text-on-color` : `bg-surface-strong text-default`,
                      )}
                    >
                      {tab.totalItems}
                    </span>
                  )}
                </div>
              </Tooltip>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  )
}
